import React, { useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { useCesium } from '../../context/CesiumContext';
import { getPanoramasByAssetId, type PanoramaPoint } from '@/queries/panoramas/list-panoramas';
import {
	Cartesian3,
	defined,
	Entity,
	HeightReference,
	NearFarScalar,
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	VerticalOrigin,
} from 'cesium';

type PanoramaLoaderProps = {
	onOpenPanorama: (sceneName: string) => void;
};

const PanoramaLoader: React.FC<PanoramaLoaderProps> = ({ onOpenPanorama }) => {
	const { viewer } = useCesium();
	const { assetId } = useParams<{ assetId: string }>();

	const entitiesRef = useRef<Entity[]>([]);
	const handlerRef = useRef<ScreenSpaceEventHandler | null>(null);

	// 🔹 Fetch panoramas based on asset
	const { data, isLoading } = useQuery({
		queryKey: ['panoramas', assetId],
		queryFn: () => getPanoramasByAssetId(assetId!),
		enabled: !!assetId,
	});

	const panoramas = useMemo(() => {
		return data?.panoramas ?? [];
	}, [data]);

	useEffect(() => {
		if (!viewer || isLoading) return;

		const currentViewer = viewer;

		// Remove old entities
		entitiesRef.current.forEach((entity) => {
			currentViewer.entities.remove(entity);
		});
		entitiesRef.current = [];

		if (!panoramas.length) return;

		const scaleByDistance = new NearFarScalar(50, 1.0, 3000, 0.4);

		panoramas.forEach((point) => {
			const position = Cartesian3.fromDegrees(point.lon, point.lat, point.alt);

			const entity = currentViewer.entities.add({
				position,
				billboard: {
					image: '/icons/360.svg',
					verticalOrigin: VerticalOrigin.BOTTOM,
					heightReference: HeightReference.CLAMP_TO_3D_TILE,
					scaleByDistance,
					disableDepthTestDistance: Number.POSITIVE_INFINITY,
					pixelOffset: new Cartesian3(0, -5, 0),
				},
				properties: {
					isPanoramaTrigger: true,
					sceneName: point.sceneName,
				},
			});

			entitiesRef.current.push(entity);
		});

		return () => {
			// CLEANUP
			if (!currentViewer || currentViewer.isDestroyed?.()) return;

			entitiesRef.current.forEach((entity) => {
				if (!entity.isDestroyed?.()) {
					currentViewer.entities.remove(entity);
				}
			});

			entitiesRef.current = [];
		};
	}, [viewer, panoramas, isLoading]);

	// Click + Hover Handler (initialized once)
	useEffect(() => {
		if (!viewer) return undefined;

		if (handlerRef.current) {
			handlerRef.current.destroy();
		}

		handlerRef.current = new ScreenSpaceEventHandler(viewer.scene.canvas);

		handlerRef.current.setInputAction((click: any) => {
			const pickedObject = viewer.scene.pick(click.position);

			if (defined(pickedObject) && pickedObject.id?.properties?.isPanoramaTrigger) {
				const sceneName = pickedObject.id.properties.sceneName.getValue();
				onOpenPanorama(sceneName);
			}
		}, ScreenSpaceEventType.LEFT_CLICK);

		let overTrigger = false;
		handlerRef.current.setInputAction((movement: any) => {
			const pickedObject = viewer.scene.pick(movement.endPosition);
			const isOver = defined(pickedObject) && !!pickedObject.id?.properties?.isPanoramaTrigger;
			if (isOver) {
				// Rewritten on every move so another handler's reset self-heals next move.
				overTrigger = true;
				viewer.canvas.style.cursor = 'pointer';
				return;
			}
			if (overTrigger) {
				overTrigger = false;
				viewer.canvas.style.cursor = '';
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);

		return () => {
			if (handlerRef.current && !handlerRef.current.isDestroyed?.()) {
				handlerRef.current.destroy();
				handlerRef.current = null;
			}
		};
	}, [viewer, onOpenPanorama]);

	return null;
};

export default PanoramaLoader;
